/**
 * Build the SVG `d` attribute for the inner pie wedge of `CircularProgress`.
 *
 * - Sweeps clockwise from 12 o'clock by `value × 360°`.
 * - Returns `null` for `value <= 0` so the caller can omit the path entirely.
 * - For `value >= 1` returns a closed full-circle path made of two semi-arcs,
 *   sidestepping the zero-length-arc artifact that appears when the SVG arc
 *   start and end coincide.
 *
 * Pure: no React, no SVG DOM. Tested directly so the math doesn't depend on
 * a render pass.
 */
export function computeWedgePath(value: number, cx: number, cy: number, r: number): string | null {
  if (value <= 0) return null
  if (value >= 1) {
    return `M ${cx} ${cy - r}`
      + ` A ${r} ${r} 0 1 1 ${cx} ${cy + r}`
      + ` A ${r} ${r} 0 1 1 ${cx} ${cy - r}`
      + ` Z`
  }
  const angle = value * Math.PI * 2
  const endX = cx + Math.sin(angle) * r
  const endY = cy - Math.cos(angle) * r
  const largeArc = value > 0.5 ? 1 : 0
  return `M ${cx} ${cy}`
    + ` L ${cx} ${cy - r}`
    + ` A ${r} ${r} 0 ${largeArc} 1 ${endX.toFixed(4)} ${endY.toFixed(4)}`
    + ` Z`
}
